export const violinSyllabus = {
  1: {
    id: 1,
    title: "Level 1 — Initial Basic (Foundation Level)",
    objective:
      "To establish instrument familiarity, correct posture, basic bow control, pitch recognition, rhythmic awareness and fundamental musical understanding on the violin.",
    practicalSections: [
      {
        title: "Instrument fundamentals",
        items: [
          "Parts of the violin (body, neck, fingerboard, strings, bridge, chin rest)",
          "Parts of the bow (frog, stick, hair, tip)",
          "Correct posture: standing or sitting position",
          "Violin hold (chin and shoulder balance)",
          "Bow hold: finger placement with relaxed grip",
          "Left-hand setup: curved fingers and thumb position",
        ],
      },
      {
        title: "Notes and playing",
        items: [
          "Open string exercises on G, D, A and E strings",
          "Straight bow movement and basic tone production",
          "Left-hand finger placement in first position",
          "Simple melodic phrases on a single string with awareness of intonation",
        ],
      },
      {
        title: "Scales (one octave, first position)",
        items: [
          "D major",
          "G major",
          "A major",
          "E minor (natural)",
          "D minor (natural)",
          "A minor (natural)",
        ],
      },
      {
        title: "Bowing techniques",
        items: [
          "Basic detaché bowing",
          "Use of whole and half bow",
          "Even bow distribution and simple bow changes",
        ],
      },
      {
        title: "Rhythm and timing",
        items: [
          "Whole, half and quarter notes",
          "Simple rhythmic patterns in 4/4 and 3/4",
          "Playing with a steady pulse",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Two short pieces: one learner’s choice and one technical or study piece",
          "Optional self-composed melody (8–16 bars) using open strings or first-position notes",
        ],
      },
    ],
    theorySections: [
      {
        title: "Music fundamentals",
        items: [
          "What is music; sound vs silence",
          "Pitch and duration",
          "High and low sounds on the violin",
        ],
      },
      {
        title: "Notes and rhythm",
        items: [
          "Musical alphabet (A–G)",
          "String names of the violin",
          "Whole, half and quarter notes and corresponding rests",
        ],
      },
      {
        title: "Intervals and scales",
        items: [
          "Whole step / half step (tone / semitone on the fingerboard)",
          "Concept of major and minor scales",
          "Ascending and descending scales and the major scale pattern (W–W–H–W–W–W–H)",
        ],
      },
      {
        title: "Harmony and notation (conceptual)",
        items: [
          "What is harmony; playing one note at a time vs multiple notes",
          "Introduction to double stops as a listening and conceptual idea",
          "Staff basics or finger-number / string-indication systems (optional)",
          "Time signatures: 2/4, 3/4, 4/4",
        ],
      },
    ],
  },
  2: {
    id: 2,
    title: "Level 2 — Intermediate (Developing Level)",
    objective:
      "To develop greater bow control, improved left-hand coordination, basic intonation accuracy, rhythmic stability and confident first-position performance.",
    practicalSections: [
      {
        title: "Instrument and technique development",
        items: [
          "Review of posture and violin hold with balanced chin–shoulder support",
          "Refined bow hold with relaxed fingers and flexible wrist",
          "Left-hand finger independence and consistent finger spacing",
          "Clean string crossings",
        ],
      },
      {
        title: "Notes and playing",
        items: [
          "Confident first-position notes on all strings",
          "Melodies using two and three strings",
          "Introduction to simple slurs (two notes per bow)",
          "Intonation awareness using open strings and reference pitches",
          "Simple expressive phrasing",
        ],
      },
      {
        title: "Scales (one octave, first position)",
        items: [
          "C major",
          "F major",
          "B♭ major",
          "G minor (natural)",
          "D minor (natural)",
          "E minor (natural)",
        ],
      },
      {
        title: "Bowing techniques",
        items: [
          "Controlled detaché",
          "Legato bowing and two-note slurs",
          "Smooth bow changes with basic control of speed and pressure",
        ],
      },
      {
        title: "Rhythm and timing",
        items: [
          "Whole, half, quarter and eighth notes and rests",
          "Simple rhythmic combinations",
          "Steady pulse with introduction to metronome practice",
          "Time signatures: 2/4, 3/4, 4/4",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Three pieces: one learner’s choice, one melodic piece with slurs and one technical / study piece",
          "Simple scale-based improvisation or original melody (12–16 bars)",
        ],
      },
    ],
    theorySections: [
      {
        title: "Rhythm and notes",
        items: [
          "Note values up to eighth notes and corresponding rests",
          "Strong and weak beats in simple meters",
        ],
      },
      {
        title: "Intervals and scales",
        items: [
          "Whole and half steps and identifying intervals on the fingerboard",
          "Major and natural minor scales and their relative relationships",
          "Scale degrees from tonic to octave",
        ],
      },
      {
        title: "Harmony and notation",
        items: [
          "Melody vs harmony as a conceptual idea",
          "Introduction to double stops (listening and concept only)",
          "Reading basic staff notation or finger-number / string indications",
          "Bar lines, repeat signs and simple time signatures",
        ],
      },
    ],
  },
  3: {
    id: 3,
    title: "Level 3 — Advanced 1 (Musical Expansion Level)",
    objective:
      "To develop secure intonation, expressive bowing, expanded rhythmic control, dynamic awareness and confident musical performance.",
    practicalSections: [
      {
        title: "Instrument and technique development",
        items: [
          "Stable posture with relaxed upper body",
          "Refined bow hold with flexible wrist and fingers",
          "Balanced left-hand frame with accurate finger placement",
          "Introduction to position awareness beyond first position (conceptual)",
        ],
      },
      {
        title: "Notes and playing",
        items: [
          "Extended first-position playing across all strings",
          "Melodies covering wider note ranges",
          "Simple position-preparation exercises",
          "Dynamic contrast (p, mf, f) and expressive phrasing",
        ],
      },
      {
        title: "Scales (one to two octaves)",
        items: [
          "D major",
          "A major",
          "G major (two octaves)",
          "C major (two octaves)",
          "E minor (harmonic)",
          "D minor (natural)",
        ],
      },
      {
        title: "Bowing techniques",
        items: [
          "Legato bowing across multiple notes",
          "Slurs (two to four notes per bow)",
          "Introduction to controlled staccato",
          "Varied bow speeds for expression and smooth string crossings",
        ],
      },
      {
        title: "Rhythm and timing",
        items: [
          "Eighth-note combinations and triplets",
          "Introduction to compound time",
          "Playing with a metronome at varied tempos",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Three pieces: one lyrical / expressive, one rhythmically driven and one contrasting style or tempo",
          "Self-arranged or original composition (16–24 bars)",
          "Simple improvisation over a given scale or rhythm",
        ],
      },
    ],
    theorySections: [
      {
        title: "Rhythm and notation",
        items: [
          "Triplets and compound rhythms",
          "Rhythmic grouping in simple and compound time",
          "Accent and articulation markings",
        ],
      },
      {
        title: "Intervals and scales",
        items: [
          "Major, minor and perfect intervals and their recognition on the violin fingerboard",
          "Major scales (up to two octaves); natural and harmonic minor scales",
          "Scale patterns and directional awareness",
        ],
      },
      {
        title: "Harmony and form",
        items: [
          "Chord functions I, IV and V as conceptual support to melody",
          "Relationship between melody and harmony and basic harmonic movement",
          "Phrase structure and binary / ternary form (listening and recognition)",
        ],
      },
      {
        title: "Notation",
        items: [
          "Intermediate staff notation or fingerings / bowings and articulation markings",
          "Repeat signs, first and second endings",
        ],
      },
    ],
  },
  4: {
    id: 4,
    title: "Level 4 — Advanced 2 (Artistic Control Level)",
    objective:
      "To achieve confident shifting, controlled vibrato, stylistic awareness, advanced bowing techniques and deeper musical expression.",
    practicalSections: [
      {
        title: "Instrument and technique development",
        items: [
          "Secure posture with freedom of movement",
          "Confident shifting between first and third positions",
          "Accurate intonation in higher positions",
          "Controlled vibrato (slow and even)",
          "Balanced coordination between left hand and bow arm",
        ],
      },
      {
        title: "Notes and playing",
        items: [
          "Melodies spanning multiple positions",
          "Position changes within musical phrases",
          "Expressive phrasing with dynamic shaping",
          "Awareness of tone projection and resonance",
        ],
      },
      {
        title: "Scales (two octaves, with shifting)",
        items: [
          "B♭ major",
          "E♭ major",
          "F major",
          "A minor (harmonic)",
          "C minor (natural)",
          "D major (including position shifts)",
        ],
      },
      {
        title: "Bowing techniques",
        items: [
          "Controlled staccato and introduction to spiccato",
          "Advanced legato phrasing",
          "Bow articulation for stylistic clarity",
        ],
      },
      {
        title: "Rhythm and timing",
        items: [
          "Compound rhythms (6/8, 9/8)",
          "Syncopated rhythmic patterns",
          "Tempo control across contrasting sections",
        ],
      },
      {
        title: "Repertoire",
        items: [
          "Four pieces including at least two contrasting styles or periods",
          "One technically demanding work and one expressive piece",
          "Self-arranged or original composition (24–32 bars)",
          "Structured improvisation over scale or harmony",
        ],
      },
    ],
    theorySections: [
      {
        title: "Harmony, modes and form",
        items: [
          "Harmonic and melodic minor scales and introduction to modes (concept and sound)",
          "Cadence types (perfect, imperfect, plagal)",
          "Secondary dominants as a conceptual harmonic device",
          "Binary, ternary and rondo form; phrase development and variation",
        ],
      },
      {
        title: "Notation",
        items: [
          "Intermediate–advanced staff notation",
          "Bowing symbols, dynamics and articulations",
          "Da capo, dal segno and coda markings",
        ],
      },
    ],
  },
  5: {
    id: 5,
    title: "Level 5 — Professional (Performance & Musical Authority)",
    objective:
      "To demonstrate professional-level technical command, stylistic versatility, expressive maturity, improvisational ability and advanced theoretical understanding.",
    practicalSections: [
      {
        title: "Technique and instrument control",
        items: [
          "Fluent shifting across multiple positions",
          "Vibrato control (speed, width and expressive use)",
          "Advanced finger independence and agility",
          "Endurance and consistency across extended performance",
        ],
      },
      {
        title: "Notes, bowing and styles",
        items: [
          "Complex melodic and technical passages with fast passagework",
          "Stylistic interpretation across genres and confident ensemble playing",
          "Bowing techniques: advanced spiccato, introductory ricochet and advanced staccato control",
        ],
      },
      {
        title: "Scales and rhythm",
        items: [
          "All major scales (two to three octaves) and all natural minor scales",
          "Harmonic and melodic minor scales in all keys",
          "Major and minor pentatonic scales and blues scales",
          "Irregular meters (5/4, 7/8) and polyrhythmic awareness",
        ],
      },
      {
        title: "Repertoire and improvisation",
        items: [
          "Five pieces including a technically virtuosic work, an extended-form piece and an original composition",
          "Multiple contrasting genres and styles",
          "Advanced improvisation over harmonic progressions",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced harmony and composition",
        items: [
          "Modulation techniques and chord function / voice leading",
          "Harmonic analysis of performed repertoire",
          "Writing idiomatically for violin and ornamentation / variation techniques",
        ],
      },
      {
        title: "Analysis",
        items: [
          "Structural and stylistic analysis",
          "Comparison of style and period features",
        ],
      },
    ],
  },
  6: {
    id: 6,
    title: "Level 6 — Mastery (Artist / Educator Level)",
    objective:
      "To certify artistic authority, complete technical freedom, original creative voice, pedagogical understanding and conceptual mastery.",
    practicalSections: [
      {
        title: "Performance and artistry",
        items: [
          "A full recital performance of approximately 30–45 minutes",
          "Multi-genre and multi-style repertoire with original compositions",
          "Advanced improvisation and ensemble or accompaniment performance",
          "Demonstration of interpretative depth and personal artistry",
        ],
      },
    ],
    theorySections: [
      {
        title: "Advanced studies and professional skills",
        items: [
          "Advanced harmonic, formal and stylistic analysis",
          "Violin pedagogy and teaching methodology; curriculum design and learner progression",
          "Music aesthetics and philosophy",
          "Practice methodology, performance psychology and artistic planning",
          "Optional research project, dissertation or lecture–recital submission",
        ],
      },
    ],
  },
};
